import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import GlobalFooter from './components/GlobalFooter';

// CSS for blob animations
const blobStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob {
    animation: blob 7s infinite;
  }
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  .animation-delay-4000 {
    animation-delay: 4s;
  }
`;

// Custom Icon Components (replacing emojis with styled SVG-like icons)
const RestaurantIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2 text-white" stroke="currentColor" strokeWidth="2">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7 2v20" strokeLinecap="round"/>
        <path d="M21 15V2v0a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-200 rounded-full"></div>
  </div>
);

const MatchIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2 text-white" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
      </svg>
    </div>
    <div className="absolute -top-1 -left-1 w-5 h-5 bg-purple-200 rounded-full"></div>
  </div>
);

const DeliveryIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2 text-white" stroke="currentColor" strokeWidth="2">
        <path d="M14 18V6a2 2 0 00-2-2H4a2 2 0 00-2 2v11a1 1 0 001 1h2"/>
        <path d="M15 18H9"/>
        <path d="M19 18h2a1 1 0 001-1v-3.65a1 1 0 00-.22-.624l-3.48-4.35A1 1 0 0017.52 8H14"/>
        <circle cx="17" cy="18" r="2"/>
        <circle cx="7" cy="18" r="2"/>
      </svg>
    </div>
    <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-green-200 rounded-full"></div>
  </div>
);

const NGOIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2 text-white" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
        <polyline points="9,22 9,12 15,12 15,22"/>
      </svg>
    </div>
    <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-200 rounded-full"></div>
  </div>
);

const HeartIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-red-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-1/2 h-1/2 text-white">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    </div>
    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-pink-200 rounded-full"></div>
  </div>
);

const VolunteerIcon = ({ className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="w-full h-full bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
      <svg viewBox="0 0 24 24" fill="none" className="w-1/2 h-1/2 text-white" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87"/>
        <path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    </div>
    <div className="absolute -top-1 -left-1 w-5 h-5 bg-teal-200 rounded-full"></div>
  </div>
);

// Animated Flow Arrow
const FlowArrow = ({ animated = true }) => (
  <div className="flex items-center justify-center px-2 md:px-4">
    <div className="relative">
      <svg width="60" height="24" viewBox="0 0 60 24" className="text-gray-300">
        <defs>
          <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
        </defs>
        <path 
          d="M0 12 L45 12 M35 4 L48 12 L35 20" 
          stroke={animated ? "url(#arrowGradient)" : "currentColor"}
          strokeWidth="3" 
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={animated ? "animate-pulse" : ""}
        />
      </svg>
      {animated && (
        <div className="absolute inset-0 flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75"></div>
        </div>
      )}
    </div>
  </div>
);

const HowItWorks = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const flowRef = useRef(null);

  // Auto-animate flow diagram
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Inject blob animation styles */}
      <style>{blobStyles}</style>
      
      {/* Hero Section with decorative elements */}
      <section className="relative py-20 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 -translate-x-1/2 translate-y-1/2"></div>
        
        {/* Decorative shapes */}
        <div className="absolute top-20 left-10 w-20 h-20 border-4 border-green-200 rounded-full opacity-50"></div>
        <div className="absolute top-40 right-20 w-12 h-12 bg-orange-200 rounded-lg rotate-45 opacity-50"></div>
        <div className="absolute bottom-20 left-1/4 w-8 h-8 bg-blue-300 rounded-full opacity-50"></div>
        
        <div className="container mx-auto px-4 max-w-5xl text-center relative z-10">
          <div className="inline-block mb-6">
            <span className="bg-green-100 text-green-700 text-sm font-semibold px-4 py-2 rounded-full">
              🌱 Zero Food Waste Initiative
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            How <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-600">Food Connect</span> Works
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Our simple three-step process connects surplus food with those who need it most, 
            ensuring <span className="font-semibold text-green-600">nothing goes waste</span> while feeding communities.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-lg px-10 py-6 rounded-full shadow-xl shadow-green-500/25 transition-all duration-300 hover:scale-105"
          >
            Get Started Today →
          </Button>
        </div>
      </section>

      {/* Main Process Flow - Enhanced Cards */}
      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Simple. <span className="text-green-500">Fast.</span> Effective.
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Three steps to transform surplus food into community nourishment
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="group">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white overflow-hidden h-full relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-orange-500"></div>
                <CardHeader className="text-center pt-10 pb-6">
                  <RestaurantIcon className="w-20 h-20 mx-auto mb-6" />
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-lg">
                    1
                  </div>
                  <CardTitle className="text-2xl font-bold">Restaurants Log Surplus</CardTitle>
                </CardHeader>
                <CardContent className="text-center pb-10">
                  <CardDescription className="text-gray-600 text-lg leading-relaxed">
                    Restaurants quickly log surplus food with details like quantity, 
                    dietary type, and pickup times. <span className="font-semibold text-orange-600">Takes less than 2 minutes.</span>
                  </CardDescription>
                </CardContent>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="group">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white overflow-hidden h-full relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-purple-500"></div>
                <CardHeader className="text-center pt-10 pb-6">
                  <MatchIcon className="w-20 h-20 mx-auto mb-6" />
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-lg">
                    2
                  </div>
                  <CardTitle className="text-2xl font-bold">Smart Matching</CardTitle>
                </CardHeader>
                <CardContent className="text-center pb-10">
                  <CardDescription className="text-gray-600 text-lg leading-relaxed">
                    Our algorithm matches surplus with NGO demands based on location, 
                    dietary preferences, and timing. <span className="font-semibold text-purple-600">Instant notifications sent.</span>
                  </CardDescription>
                </CardContent>
              </Card>
            </div>

            {/* Step 3 */}
            <div className="group">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white overflow-hidden h-full relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-400 to-green-500"></div>
                <CardHeader className="text-center pt-10 pb-6">
                  <DeliveryIcon className="w-20 h-20 mx-auto mb-6" />
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-lg shadow-lg">
                    3
                  </div>
                  <CardTitle className="text-2xl font-bold">Seamless Delivery</CardTitle>
                </CardHeader>
                <CardContent className="text-center pb-10">
                  <CardDescription className="text-gray-600 text-lg leading-relaxed">
                    Verified volunteers handle pickup and delivery with real-time 
                    tracking. <span className="font-semibold text-green-600">Average delivery time: 45 minutes.</span>
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Journey Flow Diagram */}
      <section className="py-24 relative overflow-hidden" ref={flowRef}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 left-10 w-40 h-40 border border-green-500/20 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-blue-500/20 rounded-full"></div>
          <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-purple-500/10 rounded-full blur-xl"></div>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Journey of a <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">Meal</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Follow the complete journey from surplus to service
            </p>
          </div>
          
          {/* Animated Flow Diagram */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-12 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
              {/* Step 1: Restaurant */}
              <div className={`flex flex-col items-center transition-all duration-500 ${activeStep === 0 ? 'scale-110' : 'scale-100 opacity-70'}`}>
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${activeStep === 0 ? 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-500/50' : 'bg-gradient-to-br from-orange-400/50 to-orange-600/50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" className="w-10 h-10 md:w-12 md:h-12 text-white" stroke="currentColor" strokeWidth="2">
                    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2M7 2v20M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3v7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="text-center">
                  <div className="font-bold text-white mb-1">Restaurant</div>
                  <div className="text-sm text-gray-400">Logs surplus</div>
                </div>
              </div>
              
              <FlowArrow animated={activeStep === 0} />
              
              {/* Step 2: NGO Claims */}
              <div className={`flex flex-col items-center transition-all duration-500 ${activeStep === 1 ? 'scale-110' : 'scale-100 opacity-70'}`}>
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${activeStep === 1 ? 'bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/50' : 'bg-gradient-to-br from-blue-400/50 to-blue-600/50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" className="w-10 h-10 md:w-12 md:h-12 text-white" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9,22 9,12 15,12 15,22"/>
                  </svg>
                </div>
                <div className="text-center">
                  <div className="font-bold text-white mb-1">NGO Claims</div>
                  <div className="text-sm text-gray-400">Accepts meal</div>
                </div>
              </div>
              
              <FlowArrow animated={activeStep === 1} />
              
              {/* Step 3: Volunteer */}
              <div className={`flex flex-col items-center transition-all duration-500 ${activeStep === 2 ? 'scale-110' : 'scale-100 opacity-70'}`}>
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${activeStep === 2 ? 'bg-gradient-to-br from-teal-400 to-teal-600 shadow-lg shadow-teal-500/50' : 'bg-gradient-to-br from-teal-400/50 to-teal-600/50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" className="w-10 h-10 md:w-12 md:h-12 text-white" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                </div>
                <div className="text-center">
                  <div className="font-bold text-white mb-1">Volunteer</div>
                  <div className="text-sm text-gray-400">Picks up</div>
                </div>
              </div>
              
              <FlowArrow animated={activeStep === 2} />
              
              {/* Step 4: Delivery */}
              <div className={`flex flex-col items-center transition-all duration-500 ${activeStep === 3 ? 'scale-110' : 'scale-100 opacity-70'}`}>
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${activeStep === 3 ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-lg shadow-green-500/50' : 'bg-gradient-to-br from-green-400/50 to-green-600/50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" className="w-10 h-10 md:w-12 md:h-12 text-white" stroke="currentColor" strokeWidth="2">
                    <path d="M14 18V6a2 2 0 00-2-2H4a2 2 0 00-2 2v11a1 1 0 001 1h2M15 18H9M19 18h2a1 1 0 001-1v-3.65a1 1 0 00-.22-.624l-3.48-4.35A1 1 0 0017.52 8H14"/>
                    <circle cx="17" cy="18" r="2"/>
                    <circle cx="7" cy="18" r="2"/>
                  </svg>
                </div>
                <div className="text-center">
                  <div className="font-bold text-white mb-1">Delivery</div>
                  <div className="text-sm text-gray-400">In transit</div>
                </div>
              </div>
              
              <FlowArrow animated={activeStep === 3} />
              
              {/* Step 5: Community */}
              <div className={`flex flex-col items-center transition-all duration-500 ${activeStep === 4 ? 'scale-110' : 'scale-100 opacity-70'}`}>
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${activeStep === 4 ? 'bg-gradient-to-br from-red-400 to-pink-500 shadow-lg shadow-pink-500/50' : 'bg-gradient-to-br from-red-400/50 to-pink-500/50'}`}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-12 md:h-12 text-white">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                </div>
                <div className="text-center">
                  <div className="font-bold text-white mb-1">Community</div>
                  <div className="text-sm text-gray-400">Fed & happy</div>
                </div>
              </div>
            </div>
            
            {/* Timeline indicator */}
            <div className="mt-12 flex justify-center gap-2">
              {[0, 1, 2, 3, 4].map((step) => (
                <button
                  key={step}
                  onClick={() => setActiveStep(step)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${activeStep === step ? 'bg-green-500 w-8' : 'bg-gray-600 hover:bg-gray-500'}`}
                />
              ))}
            </div>
          </div>
          
          {/* Journey Timeline */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { time: "0 min", label: "Surplus logged", color: "orange" },
              { time: "3 min", label: "NGO claims", color: "blue" },
              { time: "5 min", label: "Volunteer accepts", color: "teal" },
              { time: "15 min", label: "Pickup complete", color: "green" },
              { time: "45 min", label: "Delivered!", color: "pink" },
            ].map((item, index) => (
              <div key={index} className={`text-center p-4 rounded-xl transition-all duration-500 ${activeStep === index ? `bg-${item.color}-500/20 border border-${item.color}-500/50` : 'bg-white/5'}`}>
                <div className={`text-2xl font-bold mb-1 ${activeStep === index ? 'text-white' : 'text-gray-400'}`}>{item.time}</div>
                <div className={`text-sm ${activeStep === index ? 'text-gray-200' : 'text-gray-500'}`}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Divider - Top */}
      <div className="relative">
        <svg className="w-full h-24 -mb-1" viewBox="0 0 1440 96" fill="none" preserveAspectRatio="none">
          <path d="M0 96L60 85.3C120 75 240 53 360 48C480 43 600 53 720 58.7C840 64 960 64 1080 56C1200 48 1320 32 1380 24L1440 16V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V96Z" fill="#f9fafb"/>
        </svg>
      </div>

      {/* User Type Guides - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-gray-50"></div>
        
        {/* Decorative floating shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Orange blob - top left */}
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          {/* Blue blob - top right */}
          <div className="absolute top-40 -right-20 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          {/* Green blob - middle left */}
          <div className="absolute top-1/2 -left-40 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
          {/* Purple blob - bottom right */}
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          {/* Teal blob - bottom center */}
          <div className="absolute -bottom-20 left-1/3 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob animation-delay-2000"></div>
          
          {/* Decorative circles */}
          <div className="absolute top-32 left-1/4 w-24 h-24 border-2 border-orange-200 rounded-full opacity-40"></div>
          <div className="absolute top-60 right-1/4 w-16 h-16 border-2 border-blue-200 rounded-full opacity-40"></div>
          <div className="absolute bottom-40 left-20 w-20 h-20 border-2 border-green-200 rounded-full opacity-40"></div>
          
          {/* Dot pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)', backgroundSize: '24px 24px'}}></div>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Step-by-Step <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-500">Guides</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Detailed walkthrough for each user type
            </p>
          </div>
          
          <div className="space-y-16">
            {/* Restaurant Guide */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-100/50">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 md:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-white" stroke="currentColor" strokeWidth="2">
                      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2M7 2v20M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3v7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">For Restaurants</h3>
                    <p className="text-orange-100">Turn surplus food into community impact</p>
                  </div>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Steps */}
                  <div className="space-y-4">
                    {[
                      { step: 1, title: "Sign Up & Verify", desc: "Register with business details and license. Quick verification within 24 hours." },
                      { step: 2, title: "Log Surplus Food", desc: "Enter meal name, quantity, dietary info, and pickup window in under 2 minutes." },
                      { step: 3, title: "Auto Compliance Check", desc: "System validates FSSAI/SFA food safety standards automatically." },
                      { step: 4, title: "Track Your Impact", desc: "View real-time donation status and generate sustainability reports." },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-4 p-4 rounded-xl hover:bg-orange-50 transition-colors">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                          {item.step}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Dashboard Mockup */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-1 shadow-2xl">
                    <div className="bg-gray-900 rounded-xl overflow-hidden">
                      {/* Browser Header */}
                      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-400 text-center">
                            foodconnect.com/restaurant/dashboard
                          </div>
                        </div>
                      </div>
                      {/* Dashboard Content */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-bold text-gray-900">Restaurant Dashboard</h4>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">● Live</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="bg-white p-3 rounded-lg shadow-sm border">
                            <div className="text-2xl font-bold text-orange-500">12</div>
                            <div className="text-xs text-gray-500">Meals Listed Today</div>
                          </div>
                          <div className="bg-white p-3 rounded-lg shadow-sm border">
                            <div className="text-2xl font-bold text-green-500">60</div>
                            <div className="text-xs text-gray-500">Servings Rescued</div>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Butter Chicken (5 meals)</span>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Claimed by NGO</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>🚚 Volunteer en route</span>
                            <span>•</span>
                            <span>ETA: 15 min</span>
                          </div>
                        </div>
                        <button className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium">
                          + Log New Surplus
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* NGO Guide */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-100/50">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 md:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-white" stroke="currentColor" strokeWidth="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                      <polyline points="9,22 9,12 15,12 15,22"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">For NGOs</h3>
                    <p className="text-blue-100">Receive surplus food matched to your needs</p>
                  </div>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Steps */}
                  <div className="space-y-4">
                    {[
                      { step: 1, title: "Set Preferences", desc: "Define dietary requirements, storage capacity, and service areas." },
                      { step: 2, title: "Get Smart Alerts", desc: "Instant notifications when surplus matches your criteria." },
                      { step: 3, title: "Claim & Verify OTP", desc: "Accept donations and verify delivery with secure OTP." },
                      { step: 4, title: "Track & Report", desc: "Monitor meals received and generate impact reports." },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-4 p-4 rounded-xl hover:bg-blue-50 transition-colors">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                          {item.step}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Dashboard Mockup */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-1 shadow-2xl">
                    <div className="bg-gray-900 rounded-xl overflow-hidden">
                      {/* Browser Header */}
                      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-400 text-center">
                            foodconnect.com/ngo/dashboard
                          </div>
                        </div>
                      </div>
                      {/* Dashboard Content */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-bold text-gray-900">NGO Dashboard</h4>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">🔔 3 New</span>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-200 mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            <span className="text-sm font-medium text-blue-700">New Donation Available!</span>
                          </div>
                          <div className="text-sm text-gray-900 font-medium">Biryani - 30 servings</div>
                          <div className="text-xs text-gray-500 mb-2">📍 0.8 km away • Fresh till 6 PM</div>
                          <button className="w-full bg-blue-500 text-white py-1.5 rounded text-sm font-medium">
                            🤝 Claim This Meal
                          </button>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">Verify Delivery OTP</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Arriving</span>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <input type="text" placeholder="Enter OTP" className="flex-1 border rounded px-2 py-1 text-sm" />
                            <button className="bg-purple-500 text-white px-3 py-1 rounded text-sm">Verify</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-green-50 p-2 rounded-lg text-center">
                            <div className="text-xl font-bold text-green-600">107</div>
                            <div className="text-xs text-gray-500">Meals This Week</div>
                          </div>
                          <div className="bg-blue-50 p-2 rounded-lg text-center">
                            <div className="text-xl font-bold text-blue-600">21</div>
                            <div className="text-xs text-gray-500">Active Volunteers</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Volunteer Guide */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden border border-gray-100/50">
              <div className="bg-gradient-to-r from-green-500 to-teal-500 p-6 md:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-white" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">For Volunteers</h3>
                    <p className="text-green-100">Deliver hope and make a tangible difference</p>
                  </div>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Steps */}
                  <div className="space-y-4">
                    {[
                      { step: 1, title: "Get Verified", desc: "Quick ID verification and background check. Takes less than 24 hours." },
                      { step: 2, title: "Browse Nearby Deliveries", desc: "View available delivery requests in your area with estimated distance." },
                      { step: 3, title: "Pickup with OTP", desc: "Collect food from restaurant and verify with OTP confirmation." },
                      { step: 4, title: "Deliver & Track Impact", desc: "Complete delivery to NGO, verify with OTP, and see your impact grow." },
                    ].map((item) => (
                      <div key={item.step} className="flex items-start gap-4 p-4 rounded-xl hover:bg-green-50 transition-colors">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                          {item.step}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Dashboard Mockup */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-1 shadow-2xl">
                    <div className="bg-gray-900 rounded-xl overflow-hidden">
                      {/* Browser Header */}
                      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-400 text-center">
                            foodconnect.com/volunteer/dashboard
                          </div>
                        </div>
                      </div>
                      {/* Dashboard Content */}
                      <div className="p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-bold text-gray-900">Volunteer Dashboard</h4>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">⭐ Level 3</span>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border border-green-200 mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="text-sm font-medium text-gray-900">Salt & Pepper</div>
                              <div className="text-xs text-gray-500">5 meals • Ready for Delivery</div>
                            </div>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">NGO Claimed</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                            <span>📍 Salt & Pepper → Hope Foundation</span>
                            <span>•</span>
                            <span>2.3 km</span>
                          </div>
                          <button className="w-full bg-green-500 text-white py-1.5 rounded text-sm font-medium">
                            🚚 Claim & Deliver
                          </button>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm border mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Active Delivery</span>
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">In Progress</span>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">Your OTP: <span className="font-mono font-bold text-green-600">4287</span></div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{width: '60%'}}></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Pickup complete → Enroute to Hope Foundation</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="bg-green-50 p-2 rounded-lg text-center">
                            <div className="text-lg font-bold text-green-600">12</div>
                            <div className="text-xs text-gray-500">Deliveries</div>
                          </div>
                          <div className="bg-blue-50 p-2 rounded-lg text-center">
                            <div className="text-lg font-bold text-blue-600">60</div>
                            <div className="text-xs text-gray-500">Meals Moved</div>
                          </div>
                          <div className="bg-purple-50 p-2 rounded-lg text-center">
                            <div className="text-lg font-bold text-purple-600">4.9</div>
                            <div className="text-xs text-gray-500">Rating</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Divider - Bottom */}
      <div className="relative bg-gray-50">
        <svg className="w-full h-24 -mt-1" viewBox="0 0 1440 96" fill="none" preserveAspectRatio="none">
          <path d="M0 0L60 10.7C120 21 240 43 360 48C480 53 600 43 720 37.3C840 32 960 32 1080 40C1200 48 1320 64 1380 72L1440 80V96H1380C1320 96 1200 96 1080 96C960 96 840 96 720 96C600 96 480 96 360 96C240 96 120 96 60 96H0V0Z" fill="#f9fafb"/>
        </svg>
      </div>

      {/* Technology & Safety - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-green-50"></div>
        
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Built with <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-500">Trust & Safety</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: "🔒", 
                title: "Food Safety Compliance", 
                desc: "Automated FSSAI & SFA compliance monitoring with real-time freshness tracking",
                gradient: "from-purple-500 to-purple-600"
              },
              { 
                icon: "🤖", 
                title: "Smart Algorithms", 
                desc: "AI-powered matching for optimal food rescue based on location, diet & timing",
                gradient: "from-indigo-500 to-indigo-600"
              },
              { 
                icon: "📍", 
                title: "Real-time Tracking", 
                desc: "GPS tracking for all deliveries with temperature monitoring & confirmations",
                gradient: "from-teal-500 to-teal-600"
              },
            ].map((item, index) => (
              <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden group">
                <div className={`h-2 bg-gradient-to-r ${item.gradient}`}></div>
                <CardHeader className="text-center pt-8">
                  <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <span className="text-3xl">{item.icon}</span>
                  </div>
                  <CardTitle className="text-xl font-bold">{item.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center pb-8">
                  <CardDescription className="text-gray-600 text-base">
                    {item.desc}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-green-900 to-gray-900"></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 max-w-6xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Making <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">Real Impact</span>
          </h2>
          <p className="text-gray-400 text-lg mb-16 max-w-2xl mx-auto">
            Numbers that tell our story of change
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "1,500+", label: "Meals Rescued", icon: "🍽️" },
              { value: "41+", label: "Partner Organizations", icon: "🤝" },
              { value: "150+", label: "Active Volunteers", icon: "👥" },
              { value: "99%", label: "Success Rate", icon: "✅" },
            ].map((stat, index) => (
              <div key={index} className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1">
                  <div className="text-3xl mb-3">{stat.icon}</div>
                  <div className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-green-600 to-teal-500"></div>
        
        {/* Decorative shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-white/10 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/10 rounded-full"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-xl text-green-100 mb-10 max-w-2xl mx-auto">
            Join thousands who are already transforming surplus food into community nourishment
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-xl transition-all duration-300 hover:scale-105 font-semibold"
            >
              Join as Restaurant
            </Button>
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white/20 text-white hover:bg-white/30 text-lg px-8 py-6 rounded-full border-2 border-white/50 transition-all duration-300 hover:scale-105 font-semibold"
            >
              Join as NGO
            </Button>
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white/20 text-white hover:bg-white/30 text-lg px-8 py-6 rounded-full border-2 border-white/50 transition-all duration-300 hover:scale-105 font-semibold"
            >
              Volunteer with Us
            </Button>
          </div>
        </div>
      </section>

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

export default HowItWorks;
